import { ReactNode } from 'react';
import { Platform, Text, TextProps } from 'react-native';
import {
  TextProvider,
  useTextProvider,
} from '@/common/contexts/text/TextContext';
import { cn, tv } from '@/common/theme/tv.config';
import { textColorVariants } from '@/common/theme/variants';
import { TColors } from '@coverflex-tech/hypernova-design-tokens';

export function Label({ children, context, ...props }: TLabelProps) {
  const nativeProps =
    Platform.OS !== 'web'
      ? { suppressHighlighting: true, allowFontScaling: false }
      : {};
  const textProviderProps = useTextProvider();
  const mergedProps = {
    ...textProviderProps,
    ...props,
    className: cn(textProviderProps.className, props.className),
  };
  const componentProps = {
    ...props,
    ...nativeProps,
    className: styles.root(mergedProps),
  };

  if (context === 'provider') {
    const providerProps = {
      ...componentProps,
      className: props.className,
    };
    return (
      <TextProvider value={providerProps}>
        <Text {...componentProps}>{children}</Text>
      </TextProvider>
    );
  }

  return <Text {...componentProps}>{children}</Text>;
}

const styles = {
  root: tv({
    base: 'font-F37',
    variants: {
      size: {
        sm: 'text-sm leading-[1.4]',
        md: 'text-md leading-[1.4]',
        lg: 'text-3xl leading-[1]',
        xl: 'text-5xl leading-[1]',
      },
      bold: {
        true: 'font-F37Bold',
      },
      color: textColorVariants,
      underline: {
        true: 'underline',
      },
    },
    defaultVariants: {
      size: 'md',
      color: 'neutral30',
    },
  }),
};

export type TLabelProps = {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: keyof TColors['light']['semantic']['text'];
  bold?: boolean;
  underline?: boolean;
  className?: string;
  children?: ReactNode;
  context?: 'provider';
} & Omit<TextProps, 'onClick'>;
